// waterSoft.js
import chroma from 'chroma-js';
import { brushDefaults } from './helpers/brushLoader';
import { fillGradient } from './helpers/gradient';
import { getStrokeBounds, translatePoints, calculatePathDistances, calculatePath, interpolatePointsUniform } from './helpers/points';

// ==========================================
// 0. ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ (RNG)
// ==========================================

function seedCombine(...nums) {
  let s = 0x9e3779b9 >>> 0;
  for (let i = 0; i < nums.length; i++) {
    s = ((s ^ (nums[i] | 0)) * 0x9e3779b1) >>> 0;
  }
  return s >>> 0;
}

class FastNoise {
  constructor(seed = 1) {
    this.seed = seed;
  }

  noise2D(x, y, frequency = 1) {
    const val = Math.sin(x * frequency * 12.9898 + y * frequency * 78.233 + this.seed) * 43758.5453;
    return (val - Math.floor(val)) * 2 - 1; 
  }
}

// ==========================================
// 1. ГЕОМЕТРИЯ
// ==========================================

const defaultBrushSettings = brushDefaults.waterSoft || {};

function precomputeTrajectory(pathPts, lineWidth, totalPathLen, settings) {
  const n = pathPts.length;
  if (n === 0) return [];
  const out = new Array(n);

  const { 
    edgeStep = 2, 
    capWidth = 0.1,
    taperStart = 0.5,
    taperEnd = 0.5,
    taperStartEnabled = false,
    taperPressure = 0
  } = settings;

  const getPt = (idx) => pathPts[Math.max(0, Math.min(n - 1, idx))];

  // Сглаживание давления (Moving Average)
  const smoothedPressures = new Float32Array(n);
  for (let i = 0; i < n; i++) {
      let sum = 0;
      let count = 0;
      const range = 3;
      for(let k = -range; k <= range; k++) {
          const neighbor = pathPts[Math.max(0, Math.min(n-1, i+k))];
          if(neighbor) {
            sum += (neighbor.pressure !== undefined ? neighbor.pressure : 0.5);
            count++;
          }
      }
      smoothedPressures[i] = sum / count;
  }

  for (let i = 0; i < n; i++) {
    const p = pathPts[i];
    
    const pPrev = getPt(i - edgeStep);
    const pNext = getPt(i + edgeStep);
    let dx = pNext.x - pPrev.x;
    let dy = pNext.y - pPrev.y;
    if (dx === 0 && dy === 0) { dx = 1; dy = 0; } 

    const angle = Math.atan2(dy, dx);
    const nx = Math.cos(angle + Math.PI / 2);
    const ny = Math.sin(angle + Math.PI / 2);

    // Tapering
    let taperFactor = 1.0;
    const distStart = p.distance;
    const distEnd = totalPathLen - p.distance;

    const startLen = Math.min(totalPathLen * 0.5, Math.max(lineWidth, totalPathLen * taperStart));
    const endLen = Math.min(totalPathLen * 0.5, Math.max(lineWidth, totalPathLen * taperEnd));

    if (endLen > 0 && distEnd < endLen) {
        const t = distEnd / endLen;
        const ease = t * (2 - t); 
        taperFactor = capWidth + (1.0 - capWidth) * ease;
    } else if (taperStartEnabled && startLen > 0 && distStart < startLen) {
        const t = distStart / startLen;
        const ease = t * (2 - t);
        taperFactor = capWidth + (1.0 - capWidth) * ease;
    }

    const pVal = smoothedPressures[i];
    const finalScale = taperFactor * (1 - taperPressure + pVal * taperPressure);

    out[i] = {
      x: p.x, 
      y: p.y,
      nx, ny,
      scale: Math.max(0.01, finalScale),
      dist: p.distance,
      pressure: pVal 
    };
  }
  return out;
}


// ==========================================
// 2. ГЕНЕРАЦИЯ СЛОЕВ
// ==========================================

function generateDensityLayers(lineWidth, settings, time) {
  const layers = [];
  const seedBase = Math.floor(time * 10000);
  
  const { 
    bleeding = 0.5, 
    edgeWidth = 1.0,
    edgeOffset = 0.9,
    edgeOpacity = 0.15,
    layerSpread = 0.5,
    layerCount = 20,
    substrateSize = 1,   
  } = settings;

  const safeLayerCount = Math.max(1, Math.min(100, layerCount));
  const adaptiveAlpha = 0.06 * (20 / safeLayerCount);

  // --- 1. ПОДЛОЖКА (Base Water) ---
  // ИСПРАВЛЕНИЕ 1: Уменьшена амплитуда шума (0.3 -> 0.15)
  // Это делает подложку более стабильной и она не "вываливается" за края.
  layers.push({
      id: 'base_water',
      threshold: 0.05, 
      posOffset: 0,
      widthFactor: substrateSize,
      alpha: Math.min(0.1, adaptiveAlpha * 1.5),      
      noiseAmp: bleeding * lineWidth * 0.4, 
      isEdge: true     
  });

  // Экспонента распределения (Spread)
  // layerSpread 0 -> кучно в центре
  // layerSpread 1 -> разброс к краям
  let distributionPower;
  if (layerSpread <= 0.5) {
      distributionPower = 4.0 - (layerSpread * 6.0); 
  } else {
      distributionPower = 1.0 - ((layerSpread - 0.5) * 1.2); 
  }
  distributionPower = Math.max(0.1, distributionPower);

  // --- 2. СЛОИ ПИГМЕНТА ---
  for (let i = 0; i < safeLayerCount; i++) {
      const t = i / (safeLayerCount - 1); // 0...1
      
      // Threshold (когда слой включается)
      const threshold = Math.pow(t, 1.0);

      // ИСПРАВЛЕНИЕ 2: Привязка позиции к давлению (Centering Constraint)
      // Вместо полной случайности, мы ограничиваем, где могут появляться ПЕРВЫЕ слои.
      
      // pressureEnvelope:
      // Если t (порог) мал (0.0) -> Envelope = 0.2 (слой может быть только в центре)
      // Если t высок (1.0) -> Envelope = 1.0 (слой может быть где угодно)
      // Это гарантирует, что при слабом нажатии мы рисуем центром кисти.
      const pressureEnvelope = 0.2 + 0.8 * Math.pow(t, 0.7);

      // Генерация базовой позиции
      const noiseGen = new FastNoise(seedCombine(seedBase, i));
      let rawPos = noiseGen.noise2D(i, 100); // -1..1
      
      // Применяем Spread (кучкование)
      const sign = Math.sign(rawPos);
      const val = Math.abs(rawPos);
      let spreadPos = sign * Math.pow(val, distributionPower);

      // Применяем ограничение по давлению
      const finalPosOffset = spreadPos * pressureEnvelope;

      // Ширина линии слоя
      const rndWidth = 0.3 + 0.4 * Math.abs(noiseGen.noise2D(i, 200));
      const centerBonus = (1.0 - layerSpread) * 0.2;

      layers.push({
          id: `flow_${i}`,
          threshold: threshold,
          posOffset: finalPosOffset, 
          widthFactor: rndWidth + centerBonus, 
          alpha: adaptiveAlpha, 
          noiseAmp: bleeding * lineWidth * 0.4, 
          seed: seedCombine(seedBase, i + 500)
      });
  }

  // --- 3. АКЦЕНТЫ ПО КРАЯМ ---
  const pxWidth = Math.max(1.0, edgeWidth * (1 + Math.min(5, lineWidth / 80)));
  [-1, 1].forEach((side, idx) => {
      layers.push({
          id: `edge_${idx}`,
          threshold: 0.1,
          posOffset: side * edgeOffset, 
          widthFactor: 0.05, 
          fixedWidth: pxWidth, 
          alpha: edgeOpacity,
          noiseAmp: bleeding * lineWidth * 0.15, // Тоже уменьшено виляние краев
          seed: seedCombine(seedBase, 200 + idx),
          isStrictEdge: true
      });
  });

  return layers;
}

// ==========================================
// 3. ОТРИСОВКА
// ==========================================

export async function drawWaterSoftStroke({
  color,
  gradientColor,
  lineWidth,
  points,
  time,
  sets = {},
}, context) {
  if (!points || points.length < 2) return;

  const settings = Object.assign({}, defaultBrushSettings, sets);
  
  const bounds = getStrokeBounds(points, lineWidth * 2.0);
  if (!bounds) return;

  const localPoints = translatePoints(points, -bounds.x, -bounds.y);
  const smoothStep = Math.max(3, lineWidth / 15); 
  const smoothPoints = interpolatePointsUniform(localPoints, smoothStep);
  
  const totalPathLen = calculatePath(smoothPoints);
  const pathPoints = calculatePathDistances(smoothPoints);
  
  const trajectory = precomputeTrajectory(pathPoints, lineWidth, totalPathLen, settings);
  if (trajectory.length < 2) return;

  const bufferCanvas = document.createElement('canvas');
  bufferCanvas.width = bounds.width;
  bufferCanvas.height = bounds.height;
  const ctx = bufferCanvas.getContext('2d');
  ctx.lineCap = 'round'; 
  ctx.lineJoin = 'round';

  const layers = generateDensityLayers(lineWidth, settings, time);
  const pathsToDraw = [];
  
  layers.forEach(layer => { 

    if (!layer.fixedWidth && layer.widthFactor <= 0) { return; }

    const path = new Path2D();
    const noiseGen = new FastNoise(layer.seed || 0);
    
    let isDrawing = false;
    let lastX = 0, lastY = 0;

    // Лимит смещения для предотвращения артефактов
    const maxNoiseOffset = lineWidth * 0.6; 

    const addPt = (pt) => {
        // Шум растекания
        let noiseVal = noiseGen.noise2D(pt.dist * 0.05, layer.id === 'base_water' ? 0 : layer.posOffset * 10);
        let offsetAmt = noiseVal * layer.noiseAmp;

        // ИСПРАВЛЕНИЕ 1 (часть 2): Clamp (Ограничение) смещения
        // Даже если noiseAmp высокий, смещение не должно улетать в бесконечность
        offsetAmt = Math.max(-maxNoiseOffset, Math.min(maxNoiseOffset, offsetAmt));

        let effectiveScale = pt.scale;
        if (!layer.fixedWidth) {
             effectiveScale *= layer.widthFactor;
        }

        // Базовая координата с учетом смещения слоя
        let bx = pt.x + pt.nx * (lineWidth/2 * effectiveScale) * layer.posOffset;
        let by = pt.y + pt.ny * (lineWidth/2 * effectiveScale) * layer.posOffset;

        // Финальная координата с учетом шума
        const fx = bx + pt.nx * offsetAmt;
        const fy = by + pt.ny * offsetAmt;

        if (!isDrawing) {
            path.moveTo(fx, fy);
            isDrawing = true;
        } else {
            const mx = (lastX + fx) / 2;
            const my = (lastY + fy) / 2;
            path.quadraticCurveTo(lastX, lastY, mx, my);
        }
        lastX = fx; 
        lastY = fy;
    };

    for (let i = 0; i < trajectory.length; i++) {
        const pt = trajectory[i];
        
        // Если давление выше порога слоя - рисуем
        if (pt.pressure >= layer.threshold) {
            addPt(pt);
        } else {
            isDrawing = false;
        }
    }

    if (isDrawing) {
        path.lineTo(lastX, lastY);
    }
    
    pathsToDraw.push({
        path,
        colorInfo: layer
    });
  });

  // Рендеринг
  const chromaC = chroma(color);
  const colorOpacity = chromaC.alpha();
  const rgb = chromaC.rgb();
  const globalOpacity = settings.opacity !== undefined ? settings.opacity * colorOpacity : 1.0;

  pathsToDraw.forEach(item => {
      const layer = item.colorInfo;
      
      const a = Math.min(1, layer.alpha * globalOpacity * 2.0); 
      ctx.strokeStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;
      
      if (layer.fixedWidth) {
          ctx.lineWidth = layer.fixedWidth;
      } else {
          ctx.lineWidth = Math.max(0.5, lineWidth * layer.widthFactor * 0.8);
      }
      
      ctx.stroke(item.path);
  });

  if (gradientColor) {
    context.save();
    context.drawImage(bufferCanvas, bounds.x, bounds.y);
    context.globalCompositeOperation = 'source-atop';
    fillGradient(context, smoothPoints, color, gradientColor, lineWidth);
    context.restore();
  } else {
    context.drawImage(
        bufferCanvas, 
        0, 0, bounds.width, bounds.height,
        bounds.x, bounds.y, bounds.width, bounds.height
    );
  }
}